'use strict'

import Logger from './common/Logger'
import { MediaWorker } from '../ext/MediaWorker'
import { PeerConnection } from '../ext/PeerConnection'
import EventEmitter from './common/EventEmitter'

let globalInstanceID = 0

const logger = new Logger('CallWorker')

export const EVENTS = {
    CALL_ID_CHANGED: 'callIdChanged',
    STATE_CHANGED: 'stateChanged',
    RTC_CALL: 'rtcCall',
    RTC_CALL_ANSWER: 'rtcCallAnswer',
    RTC_CALL_RINGING: 'rtcCallRinging',
    RTC_CALL_TERMINATION: 'rtcCallTermination',
    RTC_CALL_OPTIONS_CHANGE: 'rtcCallOptionsChange',
    CALL_STATS: 'callStats',
    ADD_CANDIDATES: 'rtcCallReconnectAddCandidates',
    RTC_CALL_RECONNECT_OFFER_SDP: 'rtcCallReconnectOfferSdp',
    RTC_CALL_RECONNECT_WAIT_OFFER_SDP: 'rtcCallReconnectWaitOfferSdp',
    RTC_CALL_RECONNECT_ANSWER_SDP: 'rtcCallReconnectAnswerSdp',
}

export const rtcCallStates = {
    rtcCallStateNull: 0,
    rtcCallStateInit: 1,
    rtcCallStateBusy: 2,
    rtcCallStateConnecting: 3,
    rtcCallStateIncoming: 4,
    rtcCallStateRinging: 5,
    rtcCallStateDialing: 6,
    rtcCallStateConnected: 7,
    rtcCallStateDisconnecting: 8,
    rtcCallStateRemoteDisconnected: 9,
    rtcCallStateDisconnected: 10,
    rtcCallStateHold: 11,
    rtcCallStateCancelHold: 12,
    rtcCallStateDisconnectedTimeOut: 13,
    rtcCallStateDisconnectedDnd: 14,
    rtcCallStateDisconnectedRemote: 15,
    rtcCallStateDisconnectedBadParams: 16,
    rtcCallStateDisconnectedNotFound: 17,
    rtcCallStateDisconnectedUnknown: 18,
    rtcCallStateDisconnectedFailed: 19,
    rtcCallStateDisconnectedLocal: 20,
    rtcCallStateConferenceRedirect: 21,
    rtcCallStateAccessDenied: 22,
    rtcCallStateReconnecting: 23,
    rtcCallStateIceError: 24,
}

export const rtcCallTypes = {
    rtcCallTypeAudio: 0,
    rtcCallTypeVideo: 1,
    rtcCallTypeSip: 2,
}

// Константы интервалов
const GATHER_STATS_INTERVAL = 5000
const CHECK_ICE_STATE_INTERVAL = 2000
const PC_CREATION_DELAY = 500
const CANDIDATE_RESEND_DELAY = 100
const ANSWER_CANDIDATE_RESEND_DELAY = 200
const INDIVIDUAL_CANDIDATE_SEND_DELAY = 50

export default class CallWorker extends EventEmitter {
    constructor(callId, plugText = '', getIceServersFn) {
        super()

        // ид. инстанса
        this.instanceID = ++globalInstanceID

        // идентификаторы вызова
        this.callId = callId
        this.tempId = callId
        this.rtcCallId = callId
        this.cid = null
        this.number = null

        // свойства вызова
        this.rtcCallState = null
        this.rtcCallType = null
        this.rtcCallIncoming = false
        this.rtcCallOptions = null

        // данные вызова
        this.localCallData = null
        this.remoteCallData = null

        // медиа и соединения
        this.mediaWorker = null
        this.peerConnection = null
        this.videoPlugText = plugText
        this.getIceServersFn = getIceServersFn

        // кандидаты
        this.localCandidates = []
        this.bufferedRemoteCandidates = []
        this.remoteSdpSet = false

        // данные на ожидании
        this.callAwaitedPayload = null

        // инициируем интервалы
        this.initializeIntervals()

        logger.info(`CallWorker created with instanceID: ${this.instanceID}`)
    }

    /**
     * Инициируем интервалы
     */
    initializeIntervals() {
        this.gatherStatsId = setInterval(() => this.gatherStats(), GATHER_STATS_INTERVAL)
        this.checkIceStateId = setInterval(() => this.checkIceState(), CHECK_ICE_STATE_INTERVAL)
    }

    /**
     * Отвечаем на входящий вызов
     * @param {Object} options - опции вызова (audio, video)
     */
    async answer(options) {
        logger.info(`Answering call ${this.rtcCallId} with options: ${JSON.stringify(options)}`)

        this.setCallState(rtcCallStates.rtcCallStateConnecting)
        await this.createMedia(options)

        setTimeout(async () => {
            try {
                const offer = { type: 'offer', sdp: this.remoteCallData.sdp }
                const { sdp, candidates } = await this.createPeerConnection('answer', offer)

                this.remoteSdpSet = true
                this.localCallData = { sdp, candidates }

                // Проверяем соответствие внешних кандидатов к SDP и добавляем в случаи соответствия
                this.addMatchingRemoteCandidates(this.remoteCallData.candidates, this.remoteCallData.sdp)

                const data = {
                    callId: this.rtcCallId,
                    options: this.rtcCallOptions,
                    sdp: this.localCallData.sdp,
                    candidates: this.localCallData.candidates,
                }

                this.emit(EVENTS.RTC_CALL_ANSWER, data)
                logger.info(`Call ${this.rtcCallId} answered successfully`)
            } catch (error) {
                logger.error(error, `Failed to answer call ${this.rtcCallId}`)
                this.terminate()
            }
        }, PC_CREATION_DELAY)
    }

    /**
     * Инициализируем исходящий вызов
     * @param {Number} [cid] - ид. контакта
     * @param {Number} [number] - номер абонента
     * @param {Object} options - опции вызова (audio, video)
     */
    async call(cid, number, options) {
        if (this.callAwaitedPayload) {
            cid = this.callAwaitedPayload[0]
            number = this.callAwaitedPayload[1]
            options = this.callAwaitedPayload[2]
        }

        logger.info(`Initiating call to ${number} with cid: ${cid}`)

        this.setCallState(rtcCallStates.rtcCallStateDialing)
        await this.createMedia(options)

        const finalCid = cid || 0

        setTimeout(async () => {
            try {
                const { sdp, candidates } = await this.createPeerConnection('offer', null)

                logger.info(`Generated offer SDP with ${candidates.length} candidates`)

                const data = {
                    cid: finalCid,
                    options,
                    number,
                    sdp,
                    candidates,
                    rtcCallId: this.rtcCallId,
                }

                this.emit(EVENTS.RTC_CALL, data)
            } catch (error) {
                logger.error(error, `Failed to initiate call ${this.rtcCallId}`)
                this.terminate()
            }
        }, PC_CREATION_DELAY)
    }

    /**
     * Переподключаем исходящий вызов
     * @param {Object} options - опции вызова (audio, video)
     */
    async reconnectCall(options) {
        logger.info(`Reconnecting call ${this.rtcCallId} (instanceID: ${this.instanceID})`)

        this.setCallState(rtcCallStates.rtcCallStateReconnecting)
        await this.createMedia(options)

        setTimeout(async () => {
            try {
                const { sdp, candidates } = await this.createPeerConnection('offer', null)

                this.emit(EVENTS.RTC_CALL_RECONNECT_OFFER_SDP, { callId: this.rtcCallId, sdp })

                // Отправляем кандидаты с задержкой, защита от гонки сдп и кандидатов (условие из дока)
                setTimeout(() => {
                    this.emit(EVENTS.ADD_CANDIDATES, { callId: this.rtcCallId, candidates })
                }, CANDIDATE_RESEND_DELAY)

                logger.info(`Call ${this.rtcCallId} reconnect offer sent`)
            } catch (error) {
                logger.error(error, `Failed to reconnect call ${this.rtcCallId}`)
                this.terminate()
            }
        }, PC_CREATION_DELAY)
    }

    /**
     * Переподключаем входящий вызов
     * @param {Number} callId - ид. вызова
     */
    async reconnectIncomingCall(callId) {
        logger.info(`Reconnecting incoming call ${callId} (instanceID: ${this.instanceID})`)

        this.setCallState(rtcCallStates.rtcCallStateReconnecting)

        setTimeout(() => {
            this.emit(EVENTS.RTC_CALL_RECONNECT_WAIT_OFFER_SDP, { rtcCallId: callId })
        }, PC_CREATION_DELAY)
    }

    /**
     * Обрабатываем answer SDP для Переподключения
     * @param {Number} callId - ид. вызова
     * @param {String} remoteSdp - внешние SDP
     * @param {Object} options - опции вызова (audio, video)
     */
    async reconnectAnswerSdp(callId, remoteSdp, options) {
        logger.info(`Processing reconnect answer SDP for call ${callId} (instanceID: ${this.instanceID})`)

        this.setCallState(rtcCallStates.rtcCallStateConnecting)
        await this.createMedia(options)

        const offer = { type: 'offer', sdp: remoteSdp }

        try {
            const { sdp, candidates } = await this.createPeerConnection('answer', offer)

            setTimeout(() => {
                this.remoteSdpSet = true

                // Добавляем накопленные кандидаты, при соответствии внешнему SDP
                this.addMatchingRemoteCandidates(this.bufferedRemoteCandidates, remoteSdp)
                this.bufferedRemoteCandidates = []

                this.emit(EVENTS.RTC_CALL_RECONNECT_ANSWER_SDP, { callId: this.rtcCallId, sdp })

                // Отправляем локальные кандидаты с задержкой
                setTimeout(() => {
                    this.emit(EVENTS.ADD_CANDIDATES, { callId: this.rtcCallId, candidates })
                }, ANSWER_CANDIDATE_RESEND_DELAY)

                logger.info(`Reconnect answer SDP processed for call ${callId}`)
            }, PC_CREATION_DELAY)
        } catch (error) {
            logger.error(error, `Failed to process reconnect answer SDP for call ${callId}`)
            this.terminate()
        }
    }

    /**
     * Обработка данных успешного исходящего вызова
     * @param {Error} error - ошибка вызова
     * @param {Object} result - данные по вызову
     */
    onCallResult(error, result = {}) {
        if (error) {
            logger.error(error, `Call result error: ${error.error}`)
            return
        }

        const { callId, options, cid, number, sdp, candidates } = result
        this.rtcCallIncoming = false
        this.setRtcCallId(callId, options, cid, number)
        this.localCallData = { sdp, candidates }

        logger.info(`Call result processed for call ${callId}`)
    }

    /**
     * Обработка входящего вызова
     * @param {String} cid - ид. контакта
     * @param {Number} callId - ид. вызова
     * @param {Object} options - опции вызова (audio, video)
     * @param {String} number - номер абонента
     * @param {String} sdp - внешние SDP
     * @param {Array} candidates - внешние кандидаты
     */
    called(cid, callId, options, number, sdp, candidates) {
        logger.info(`Incoming call from ${number} (cid: ${cid}, callId: ${callId})`)

        this.emit(EVENTS.RTC_CALL_RINGING, callId)
        this.remoteCallData = { sdp, candidates }
        this.rtcCallIncoming = true
        this.setRtcCallId(callId, options, cid, number)
        this.setCallState(rtcCallStates.rtcCallStateIncoming)
    }

    /**
     * Разрушаем вызов
     */
    callTerminate() {
        if (this.rtcCallId) {
            this.emit(EVENTS.RTC_CALL_TERMINATION, { callId: this.rtcCallId })
            logger.info(`Call ${this.rtcCallId} terminated`)
        }
        this.setCallState(rtcCallStates.rtcCallStateDisconnected)
        this.terminate()
    }

    /**
     * Разрушаем вызов без смены статуса, для переподключения
     */
    destroy() {
        logger.info(`Destroying CallWorker instance ${this.instanceID}`)
        this.terminate()
    }

    /**
     * Обрабатываем уведомление начала вызова от удаленной стороны
     * @param {Number} callId - ид. вызова
     */
    callRinged(callId) {
        if (this.rtcCallId === callId) {
            this.setCallState(rtcCallStates.rtcCallStateRinging)
            logger.info(`Call ${callId} is ringing`)
        }
    }

    /**
     * Обработка ответа на вызов удаленной стороной
     * @param {Number} callId - ид. вызова
     * @param {String} sdp - внешние SDP
     * @param {Array} [candidates] - внешние кандидаты
     */
    callAnswered(callId, sdp, candidates = []) {
        logger.info(`Call ${callId} answered (instanceID: ${this.instanceID})`)

        this.setCallState(rtcCallStates.rtcCallStateConnecting)

        if (this.rtcCallId === callId) {
            this.remoteCallData = { sdp, candidates }
            const answer = { type: 'answer', sdp }

            this.peerConnection.setAnswer(answer)
            this.remoteSdpSet = true

            // Добавляем накопленные кандидаты, при соответствии внешнему SDP
            this.addMatchingRemoteCandidates(this.bufferedRemoteCandidates, sdp)
            this.bufferedRemoteCandidates = []

            // Добавляем переданные кандидаты, при соответствии внешнему SDP
            this.addMatchingRemoteCandidates(this.remoteCallData.candidates, sdp)

            // Повторная отправка кандидатов (из дока)
            setTimeout(() => {
                this.emit(EVENTS.ADD_CANDIDATES, { callId: this.rtcCallId, candidates: this.localCandidates })
            }, CANDIDATE_RESEND_DELAY)
        }
    }

    /**
     * Обработка причины завершения вызова
     * @param {Number} callId - ид. вызова
     * @param {String} reason - причина завершения
     */
    callTerminated(callId, reason) {
        if (this.rtcCallId !== callId) return

        logger.info(`Call ${callId} terminated with reason: ${reason}`)

        const stateMap = {
            'timeout': rtcCallStates.rtcCallStateDisconnectedTimeOut,
            'dnd': rtcCallStates.rtcCallStateDisconnectedDnd,
            'remote': rtcCallStates.rtcCallStateDisconnectedRemote,
            'bad-params': rtcCallStates.rtcCallStateDisconnectedBadParams,
            'not-found': rtcCallStates.rtcCallStateDisconnectedNotFound,
            'unknown': rtcCallStates.rtcCallStateDisconnectedUnknown,
            'failed': rtcCallStates.rtcCallStateDisconnectedFailed,
            'local': rtcCallStates.rtcCallStateDisconnectedLocal,
            'conference-redirect': rtcCallStates.rtcCallStateConferenceRedirect,
            'access-denied': rtcCallStates.rtcCallStateAccessDenied,
        }

        const state = stateMap[reason] || rtcCallStates.rtcCallStateRemoteDisconnected
        this.setCallState(state)
        this.terminate()
    }

    /**
     * Ставим/снимем холд вызова
     * @param {Boolean} value - ставим/снимем
     * @param {Boolean} remote - холд удаленной стороной
     */
    callHold(value, remote = true) {
        if (!this.mediaWorker) return

        const isVideo = (this.rtcCallType === rtcCallTypes.rtcCallTypeVideo)
        this.mediaWorker.hold(value, isVideo)

        if (remote) {
            const isHold = this.mediaWorker.getIsHold()
            const state = isHold ? rtcCallStates.rtcCallStateHold : rtcCallStates.rtcCallStateCancelHold
            this.setCallState(state)
        }

        logger.info(`Call ${this.rtcCallId} hold state changed to: ${value}`)
    }

    /**
     * Добавляем внешние кандидаты
     * @param {Object} candidate - кандидаты
     */
    addRemoteIceCandidate(candidate) {
        logger.info(`Adding remote ICE candidate (instanceID: ${this.instanceID})`)

        if (this.remoteSdpSet) {
            this.peerConnection?.addCandidate(candidate)
        } else {
            this.bufferedRemoteCandidates.push(candidate)
        }
    }

    /**
     * Ставим вызов на ожидание свободной  линии
     * @param {...any} payload - параметры вызова
     */
    setCallFreeAwaiting(...payload) {
        this.callAwaitedPayload = payload
        this.setCallState(rtcCallStates.rtcCallStateBusy)
        logger.info(`Call set to waiting state`)
    }

    /**
     * Ставим/снимем вызов на паузу
     * @param {Boolean} value - ставим/снимем
     */
    pause(value) {
        const options = value
            ? { audio: true, video: false, hold: true }
            : { audio: true, video: false, hold: false }

        this.callHold(value, false)
        this.emit(EVENTS.RTC_CALL_OPTIONS_CHANGE, { callId: this.rtcCallId, options })
    }

    /**
     * Обновляем медиа стрим
     */
    updateStreams() {
        if (!this.mediaWorker) return

        if (this.rtcCallType === rtcCallTypes.rtcCallTypeVideo) {
            this.mediaWorker.updateStreams('#localVideo', '#remoteVideo')
        } else {
            this.mediaWorker.updateStreams('#localAudio', '#remoteAudio')
        }
    }

    /**
     * Меняем уровень громкости
     * @param {Number} volume - уровень громкости
     */
    changeVolumeEvent(volume) {
        this.mediaWorker?.changeVolumeEvent(volume)
    }

    /**
     * Ставим/снимем mute
     * @returns {Boolean} новое значение
     */
    mediaWorkerMute() {
        return this.mediaWorker?.mute() || false
    }

    // Getters
    getCallState() {
        return this.rtcCallState || rtcCallStates.rtcCallStateNull
    }

    getCallType() {
        return this.rtcCallType
    }

    getCallId() {
        return this.rtcCallId
    }

    getCid() {
        return this.cid
    }

    getNumber() {
        return this.number
    }

    getLocalCandidates() {
        return this.localCandidates
    }

    // Setters
    setCallState(state) {
        this.rtcCallState = state
        logger.info(`Call ${this.rtcCallId || this.tempId} state changed to: ${state}`)
        this.emit(EVENTS.STATE_CHANGED, {
            callId: this.rtcCallId || this.tempId,
            cid: this.cid,
            number: this.number,
            state,
            rtcCallType: this.rtcCallType
        })
    }

    setCallId(callId) {
        const oldId = this.rtcCallId
        if (this.rtcCallId) {
            this.tempId = this.rtcCallId
        }
        this.rtcCallId = callId
        this.emit(EVENTS.CALL_ID_CHANGED, { oldId, newId: this.rtcCallId })
    }

    /**
     * Создание MediaWorker и инициализация локального стрима
     * @param {Object} options - опции вызова (audio, video)
     */
    async createMedia(options) {
        const mediaElement = options.video ? '#localVideo' : '#localAudio'
        const remoteElement = options.video ? '#remoteVideo' : '#remoteAudio'

        this.mediaWorker = new MediaWorker(mediaElement, remoteElement, this.videoPlugText)

        try {
            await this.mediaWorker.startLocal(options)
            logger.info(`Media initialized for call ${this.rtcCallId}`)
        } catch (error) {
            logger.error(error, `Failed to initialize media for call ${this.rtcCallId}`)
            throw error
        }
    }

    /**
     * Установка данных по вызову
     * @param {String} id - ид. вызова
     * @param {Object} options - опции вызова (audio, video)
     * @param {String} cidA - ид. контакта
     * @param {String} numberA - номер абонента
     */
    setRtcCallId(id, options, cidA, numberA) {
        this.cid = cidA
        this.number = numberA
        this.setCallId(id)
        this.rtcCallOptions = options

        if (this.rtcCallId) {
            // определяем тип вызова по данным
            if (this.rtcCallOptions.video) {
                this.rtcCallType = rtcCallTypes.rtcCallTypeVideo
            } else if (!this.number) {
                this.rtcCallType = rtcCallTypes.rtcCallTypeAudio
            } else {
                this.rtcCallType = rtcCallTypes.rtcCallTypeSip
            }
        } else {
            // сбрасываем свойства, если неи rtcCallId
            this.cleanupCall()
        }
    }

    /**
     * Подготавливаем список ICE серверов
     * @param {Array} iceServers - список ICE серверов
     * @return {[]}
     */
    sanitizeIceServers(iceServers) {
        return (iceServers || []).map(server => {
            if (!server) return null

            const urls = server.url || server.urls
            if (!urls || (typeof urls !== 'string' && !Array.isArray(urls))) return null

            const result = { urls } // Safari "url" не поддерживает

            if (server.username) result.username = server.username
            if (server.credential) result.credential = server.credential

            return result
        }).filter((val) => val)
    }

    /**
     * Создаем PeerConnection и SDP
     * @param {String} type - 'offer' | 'answer'
     * @param {Object} setOffer - offer
     * @returns {Promise<Object>} SDP, candidates
     */
    createPeerConnection(type, setOffer) {
        return new Promise(async (resolve, reject) => {
            let isDone = false
            const candidates = []
            let sdp = null

            if (!this.mediaWorker) {
                resolve({ sdp, candidates })
                return
            }

            try {
                const iceServers = this.sanitizeIceServers(await this.getIceServersFn())
                this.peerConnection = new PeerConnection(
                    iceServers,
                    this.mediaWorker.getLocalStream(),
                    // candidate callback
                    (candidate) => {
                        if (!candidate) return

                        if (!isDone) {
                            candidates.push(candidate)
                        } else {
                            // Досылка кандидатов после иницилизации
                            setTimeout(() => {
                                this.emit(EVENTS.ADD_CANDIDATES, {
                                    callId: this.rtcCallId,
                                    candidates: [candidate]
                                })
                            }, INDIVIDUAL_CANDIDATE_SEND_DELAY)
                        }
                    },
                    // onRemoteStream
                    (data) => {
                        if (data.connectingState) {
                            this.setCallState(rtcCallStates.rtcCallStateConnected)
                        } else if (data.stream) {
                            this.mediaWorker.setRemoteStream(data.stream)
                        }
                    }
                )

                // Генерация SDP для типов offer | answer
                switch (type) {
                    case 'offer':
                        this.peerConnection.getOffer((offer) => {
                            if (offer?.sdp) {
                                sdp = offer.sdp
                            }
                        })
                        break
                    case 'answer':
                        this.peerConnection.getAnswer(setOffer, (answer) => {
                            if (answer?.sdp) {
                                sdp = answer.sdp
                            }
                        })
                        break
                }

                // Таймаут для накопления кандидатов
                setTimeout(() => {
                    isDone = true
                    this.localCandidates = candidates
                    resolve({ sdp, candidates })
                }, PC_CREATION_DELAY)

            } catch (error) {
                logger.error(error, `Failed to create PeerConnection for call ${this.rtcCallId}`)
                reject(error)
            }
        })
    }

    /**
     * Добавляем кандидатов совпадающих с SDP
     * @param {Array} candidates - кандидаты
     * @param {String} sdp - внешний SDP
     */
    addMatchingRemoteCandidates(candidates, sdp) {
        candidates.forEach(candidate => {
            if (this.checkCandidateMatchSdp(candidate, sdp)) {
                this.peerConnection?.addCandidate(candidate)
            }
        })
    }

    /**
     * Проверка соответсвия кандидато к SDP
     * @param {Object} candidate - кандидат
     * @param {String} sdp - SDP
     * @returns {Boolean} соответствует?
     */
    checkCandidateMatchSdp(candidate, sdp) {
        const uFrag = this.getSDPParam("a=ice-ufrag:", sdp)
        const usernameFragment = candidate.usernameFragment
        const candidateString = candidate.candidate

        if (!candidateString ||
            (usernameFragment !== uFrag &&
                candidateString.indexOf(uFrag) < 0)) {
            logger.info(`Candidate does not match SDP: ${JSON.stringify(candidate)}`)
            return false
        }

        return true
    }

    /**
     * Поиск параметра в SDP
     * @param {String} param - что ищем
     * @param {String} sdp - SDP
     * @returns {String|null} value
     */
    getSDPParam(param, sdp) {
        if (!param || !sdp) return null

        const foundPos = sdp.indexOf(param) + param.length
        if (foundPos === -1) return null

        let foundEnd = sdp.indexOf('\r\n', foundPos)
        if (foundEnd === -1) foundEnd = sdp.length

        return sdp.substring(foundPos, foundEnd)
    }

    /**
     * Сбор статистики
     */
    gatherStats() {
        if (!this.mediaWorker || !this.peerConnection) return

        const stats = this.peerConnection.getCallStats?.()
        if (!stats) return

        stats.callId = this.rtcCallId
        stats.peerMuted = this.mediaWorker.muted
        stats.peerHold = this.mediaWorker.getIsHold()
        stats.peerRemoteHold = this.rtcCallState === rtcCallStates.rtcCallStateHold

        this.emit(EVENTS.CALL_STATS, stats)
    }

    /**
     * Проверка состояния ICE connection
     */
    checkIceState() {
        if (!this.peerConnection) return

        const pcIceState = this.peerConnection.getIceState()
        if (pcIceState === 'disconnected') {
            logger.info(`ICE connection disconnected for call ${this.rtcCallId}`)
            this.setCallState(rtcCallStates.rtcCallStateIceError)
        }
    }

    /**
     * Разрушаем PeerConnection
     */
    destroyPeerConnection() {
        if (this.peerConnection) {
            this.peerConnection.close()
            this.peerConnection = null
            logger.info(`PeerConnection destroyed for call ${this.rtcCallId}`)
        }
    }

    /**
     * Останавливаем mediaWorker и чистим свойства звонка
     */
    cleanupCall() {
        if (this.mediaWorker) {
            this.mediaWorker.stop()
            this.mediaWorker = null
        }

        this.cid = null
        this.number = null
        this.rtcCallState = null
        this.rtcCallType = null
        this.localCallData = null
        this.remoteCallData = null
        this.localCandidates = []
        this.bufferedRemoteCandidates = []
        this.remoteSdpSet = false
    }

    /**
     * Разрушаем вызов и чистим свойства
     */
    terminate() {
        logger.info(`Terminating call ${this.rtcCallId || this.tempId}`)

        // Чистим интервалы
        if (this.gatherStatsId) {
            clearInterval(this.gatherStatsId)
            this.gatherStatsId = null
        }

        if (this.checkIceStateId) {
            clearInterval(this.checkIceStateId)
            this.checkIceStateId = null
        }

        // Чистим ресурсы
        this.destroyPeerConnection()
        this.setRtcCallId(null)
        this.clearListeners()

        logger.info(`CallWorker instance ${this.instanceID} terminated`)
    }
}